/**
 * certificateSmartMatch.js
 *
 * Shared smart-matching utilities for certificate uploads.
 * Matches AI-extracted person names and course titles against
 * a space's team members and requirements using multi-tier scoring.
 */

import { levenshtein, fuzzyNameScore } from '@/pages/import-matrix/utils/matchUsers';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STOP_WORDS = new Set([
  'the', 'and', 'of', 'a', 'an', 'in', 'for', 'to', 'on', 'at', 'by', 'with',
]);

const NOISE_WORDS = new Set([
  'training', 'course', 'certification', 'certificate',
  'program', 'programme', 'level', 'award',
]);

const PUNCTUATION_RE = /[,\-–—:;.'""''()&/\\]/g;

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * Basic normalisation: lowercase, trim, collapse whitespace.
 */
function normalize(str) {
  if (!str) return '';
  return str.toLowerCase().trim().replace(/\s+/g, ' ');
}

/**
 * Aggressive normalisation for comparison: strip stop words, noise words, and punctuation.
 */
function normalizeForComparison(str) {
  if (!str) return '';
  return str
    .toLowerCase()
    .replace(PUNCTUATION_RE, ' ')
    .split(/\s+/)
    .filter(w => w && !NOISE_WORDS.has(w) && !STOP_WORDS.has(w))
    .join(' ')
    .trim();
}

/**
 * Extract abbreviations from parenthetical text in a string.
 * e.g. "CITB - (TWS) Temporary Works Supervisor" -> ["TWS"]
 */
function extractAbbreviations(str) {
  if (!str) return [];
  const matches = [];
  const re = /\(([^)]+)\)/g;
  let m;
  while ((m = re.exec(str)) !== null) {
    const abbr = m[1].trim();
    if (abbr.length >= 2) {
      matches.push(abbr);
    }
  }
  return matches;
}

/**
 * Tokenise a string into meaningful words, stripping stop words and noise words.
 * Only keeps words with 2+ characters.
 */
function getMeaningfulWords(str) {
  if (!str) return [];
  return str
    .toLowerCase()
    .replace(PUNCTUATION_RE, ' ')
    .split(/\s+/)
    .filter(w => w.length >= 2 && !STOP_WORDS.has(w) && !NOISE_WORDS.has(w));
}

/**
 * Compute word-overlap score: matched words / min(len1, len2).
 * Uses the smaller set as denominator so that a short extracted title
 * can fully match against a longer requirement name.
 */
function wordOverlapScore(words1, words2) {
  if (!words1.length || !words2.length) return 0;
  const set2 = new Set(words2);
  let matches = 0;
  for (const w of words1) {
    if (set2.has(w)) matches++;
  }
  const denominator = Math.min(words1.length, words2.length);
  return matches / denominator;
}

// ---------------------------------------------------------------------------
// matchMember
// ---------------------------------------------------------------------------

/**
 * Score a single member against extracted name data.
 * Returns { score, tier, matchedFields } or null if no match.
 */
function scoreMember(member, nameCombinations) {
  let bestResult = null;

  for (const { first, last } of nameCombinations) {
    const fullSearch = normalize([first, last].filter(Boolean).join(' '));
    if (!fullSearch) continue;

    const memberName = normalize(
      member.name || [member.first_name, member.last_name].filter(Boolean).join(' ')
    );
    if (!memberName) continue;

    const firstLower = normalize(first);
    const lastLower = normalize(last);
    let score = 0;
    let tier = null;
    let matchedFields = [];

    // Tier 1: Exact match (either name order)
    if (firstLower && lastLower) {
      if (
        memberName === `${firstLower} ${lastLower}` ||
        memberName === `${lastLower} ${firstLower}`
      ) {
        score = 1.0;
        tier = 'exact';
        matchedFields = ['first_name', 'last_name'];
      }
    }

    // Tier 2: Word-boundary match (both names present as whole words)
    if (score === 0 && firstLower && lastLower) {
      const memberWords = memberName.split(/\s+/);
      const firstMatch = memberWords.some(w => w === firstLower);
      const lastMatch = memberWords.some(w => w === lastLower);
      if (firstMatch && lastMatch) {
        score = 0.9;
        tier = 'word_boundary';
        matchedFields = ['first_name', 'last_name'];
      } else if (firstMatch) {
        // Partial — only first name matched as word boundary
        score = 0.5;
        tier = 'partial';
        matchedFields = ['first_name'];
      } else if (lastMatch) {
        score = 0.5;
        tier = 'partial';
        matchedFields = ['last_name'];
      }
    }

    // Tier 3: Fuzzy name match via Levenshtein
    if (score < 0.6) {
      const fuzzy = fuzzyNameScore(fullSearch, memberName);
      if (fuzzy >= 0.6 && fuzzy > score) {
        score = fuzzy;
        tier = 'fuzzy';
        // Determine which parts contributed
        matchedFields = [];
        if (firstLower) {
          const memberWords = memberName.split(/\s+/);
          const firstFuzzy = memberWords.some(w => {
            const maxLen = Math.max(w.length, firstLower.length);
            return maxLen > 0 && (1 - levenshtein(w, firstLower) / maxLen) >= 0.7;
          });
          if (firstFuzzy) matchedFields.push('first_name');
        }
        if (lastLower) {
          const memberWords = memberName.split(/\s+/);
          const lastFuzzy = memberWords.some(w => {
            const maxLen = Math.max(w.length, lastLower.length);
            return maxLen > 0 && (1 - levenshtein(w, lastLower) / maxLen) >= 0.7;
          });
          if (lastFuzzy) matchedFields.push('last_name');
        }
        if (!matchedFields.length) matchedFields = ['full_name'];
      }
    }

    if (score > 0 && (!bestResult || score > bestResult.score)) {
      bestResult = { score, tier, matchedFields };
    }
  }

  return bestResult;
}

/**
 * Build name combinations from extracted data.
 */
function buildNameCombinations(extractedData) {
  const firstNames = toArray(extractedData.first_name).filter(Boolean);
  const lastNames = toArray(extractedData.last_name).filter(Boolean);

  if (!firstNames.length && !lastNames.length) return [];

  const combos = [];
  for (const fn of firstNames) {
    for (const ln of lastNames) {
      if (fn || ln) combos.push({ first: fn || '', last: ln || '' });
    }
  }
  if (!lastNames.length) {
    for (const fn of firstNames) {
      combos.push({ first: fn, last: '' });
    }
  }
  return combos;
}

/**
 * Match AI-extracted person names against team members using multi-tier scoring.
 * Returns only the best matching member (backwards compatible).
 */
export function matchMember(extractedData, teamMembers) {
  if (!extractedData || !teamMembers?.length) return null;
  const result = matchMemberWithCandidates(extractedData, teamMembers);
  return result.best;
}

/**
 * Match AI-extracted person names against team members, returning all scored
 * candidates with match details for UI display.
 *
 * If the top two candidates are both strong and close in score, the match is
 * flagged as ambiguous and `best` is null — the user must pick.
 *
 * @param {{ first_name?: string|string[], last_name?: string|string[] }} extractedData
 * @param {Array<{ id: string, name?: string, first_name?: string, last_name?: string }>} teamMembers
 * @returns {{ best: Object|null, candidates: Array<{ member: Object, score: number, tier: string, matchedFields: string[] }>, ambiguous: boolean }}
 */
export function matchMemberWithCandidates(extractedData, teamMembers, options = {}) {
  const { maxCandidates = 5 } = options;
  const empty = { best: null, candidates: [], ambiguous: false };
  if (!extractedData || !teamMembers?.length) return empty;

  const nameCombinations = buildNameCombinations(extractedData);
  if (!nameCombinations.length) return empty;

  const scored = [];

  for (const member of teamMembers) {
    const result = scoreMember(member, nameCombinations);
    if (result && result.score > 0) {
      scored.push({
        member,
        score: result.score,
        tier: result.tier,
        matchedFields: result.matchedFields
      });
    }
  }

  // Sort descending by score
  scored.sort((a, b) => b.score - a.score);

  const candidates = scored.slice(0, maxCandidates);

  // Ambiguity: top 2 both strong (>= 0.85) AND within 0.05 of each other
  const top = scored[0];
  const second = scored[1];
  const ambiguous = !!(top && second && top.score >= 0.85 && (top.score - second.score) < 0.05);

  return {
    best: !ambiguous && top && top.score >= 0.6 ? top.member : null,
    candidates,
    ambiguous
  };
}

// ---------------------------------------------------------------------------
// matchRequirement
// ---------------------------------------------------------------------------

/**
 * Score a single requirement against course titles.
 * Returns { score, tier, matchedWords } or null.
 */
function scoreRequirement(req, courseTitles, courseNameMap) {
  const matchableNames = [req.name];
  if (courseNameMap && req.course_link_id) {
    const linkedName = courseNameMap.get(req.course_link_id);
    if (linkedName) matchableNames.push(linkedName);
  }

  let bestResult = null;

  for (const courseTitle of courseTitles) {
    const normCourse = normalize(courseTitle);
    const normCourseStripped = normalizeForComparison(courseTitle);
    const courseWords = getMeaningfulWords(courseTitle);

    for (const reqName of matchableNames) {
      if (!reqName) continue;

      const normReq = normalize(reqName);
      const normReqStripped = normalizeForComparison(reqName);
      const reqWords = getMeaningfulWords(reqName);
      const abbreviations = extractAbbreviations(reqName);

      let score = 0;
      let tier = null;
      let matchedWords = [];

      // Tier 1: Exact match
      if (normCourse === normReq) {
        score = 1.0;
        tier = 'exact';
        matchedWords = reqWords;
      }

      // Tier 2: Contains match (either direction)
      if (score === 0) {
        if (normReq.includes(normCourse) || normCourse.includes(normReq)) {
          const lenDiff = Math.abs(normCourse.length - normReq.length);
          score = 0.85 - lenDiff * 0.0005;
          if (score < 0.80) score = 0.80;
          tier = 'contains';
          // Words from the course title that appear in the requirement
          matchedWords = courseWords.filter(w => reqWords.includes(w));
        }
      }

      // Tier 3: Normalised contains (noise words + punctuation stripped)
      if (score === 0 && normCourseStripped && normReqStripped) {
        if (
          normReqStripped.includes(normCourseStripped) ||
          normCourseStripped.includes(normReqStripped)
        ) {
          const lenDiff = Math.abs(normCourseStripped.length - normReqStripped.length);
          score = 0.75 - lenDiff * 0.0005;
          if (score < 0.70) score = 0.70;
          tier = 'normalized_contains';
          matchedWords = courseWords.filter(w => reqWords.includes(w));
        }
      }

      // Tier 4: Abbreviation match
      if (score === 0 && abbreviations.length > 0) {
        for (const abbr of abbreviations) {
          if (normalize(courseTitle) === normalize(abbr)) {
            score = 0.70;
            tier = 'abbreviation';
            matchedWords = [abbr.toLowerCase()];
            break;
          }
        }
      }

      // Tier 5: Word overlap
      if (score === 0 && courseWords.length >= 2 && reqWords.length >= 2) {
        const overlap = wordOverlapScore(courseWords, reqWords);
        const sharedCount = Math.round(overlap * Math.min(courseWords.length, reqWords.length));
        if (overlap >= 0.6 && sharedCount >= 2) {
          score = 0.45 + 0.35 * overlap;
          tier = 'word_overlap';
          matchedWords = courseWords.filter(w => reqWords.includes(w));
        }
      }

      // Tier 6: Levenshtein character-level similarity
      if (score === 0 && normCourseStripped && normReqStripped) {
        const maxLen = Math.max(normCourseStripped.length, normReqStripped.length);
        if (maxLen > 0) {
          const dist = levenshtein(normCourseStripped, normReqStripped);
          const similarity = 1 - dist / maxLen;
          if (similarity >= 0.7) {
            score = 0.4 + 0.25 * similarity;
            tier = 'levenshtein';
            matchedWords = courseWords.filter(w =>
              reqWords.some(rw => {
                const ml = Math.max(w.length, rw.length);
                return ml > 0 && (1 - levenshtein(w, rw) / ml) >= 0.7;
              })
            );
          }
        }
      }

      if (score > 0 && (!bestResult || score > bestResult.score)) {
        bestResult = { score, tier, matchedWords };
      }
    }
  }

  return bestResult;
}

/**
 * Match AI-extracted course titles against requirements using multi-tier scoring.
 * Returns only the best matching requirement (backwards compatible).
 */
export function matchRequirement(extractedData, requirements, options = {}) {
  if (!extractedData || !requirements?.length) return null;
  const result = matchRequirementWithCandidates(extractedData, requirements, options);
  return result.best;
}

/**
 * Match AI-extracted course titles against requirements, returning all scored
 * candidates with match details for UI display.
 *
 * @param {{ course_title?: string|string[] }} extractedData
 * @param {Array<{ id: string, name: string, course_link_id?: string, course_link_type?: string }>} requirements
 * @param {{ courseNameMap?: Map<string, string> }} [options]
 * @returns {{ best: Object|null, candidates: Array<{ requirement: Object, score: number, tier: string, matchedWords: string[] }> }}
 */
export function matchRequirementWithCandidates(extractedData, requirements, options = {}) {
  const empty = { best: null, candidates: [] };
  if (!extractedData || !requirements?.length) return empty;

  const courseTitles = toArray(extractedData.course_title).filter(Boolean);
  if (!courseTitles.length) return empty;

  const { courseNameMap = null, maxCandidates = 5 } = options;

  const scored = [];

  for (const req of requirements) {
    const result = scoreRequirement(req, courseTitles, courseNameMap);
    if (result && result.score > 0) {
      scored.push({
        requirement: req,
        score: result.score,
        tier: result.tier,
        matchedWords: result.matchedWords
      });
    }
  }

  // Sort descending by score
  scored.sort((a, b) => b.score - a.score);

  const candidates = scored.slice(0, maxCandidates);

  return {
    best: scored.length > 0 && scored[0].score >= 0.50 ? scored[0].requirement : null,
    candidates
  };
}

// ---------------------------------------------------------------------------
// Free-text query helpers (for live search)
// ---------------------------------------------------------------------------

/**
 * Parse a free-text name query into first/last components.
 * Handles "First Last", "Last, First", or single names.
 */
function parseNameQuery(text) {
  if (!text) return { first: '', last: '' };
  const trimmed = text.trim();
  if (!trimmed) return { first: '', last: '' };

  if (trimmed.includes(',')) {
    const [last, ...rest] = trimmed.split(',').map(s => s.trim());
    return { first: rest.join(' '), last };
  }

  const parts = trimmed.split(/\s+/);
  if (parts.length === 1) return { first: parts[0], last: '' };
  return { first: parts[0], last: parts.slice(1).join(' ') };
}

/**
 * Score all members against a free-text query, returning ranked candidates.
 * Combines semantic (matcher) scoring with substring fallback so user-typed text
 * always surfaces matching names even if they wouldn't auto-match.
 *
 * @param {string} query
 * @param {Array} members
 * @param {{ maxResults?: number }} [options]
 * @returns {Array<{ member, score, tier, matchedFields }>}
 */
export function scoreMembersByQuery(query, members, options = {}) {
  const { maxResults = 12 } = options;
  if (!query || !members?.length) return [];

  const { first, last } = parseNameQuery(query);
  const matcherInput = { first_name: first, last_name: last };

  // Score everything via the matcher
  const result = matchMemberWithCandidates(matcherInput, members, { maxCandidates: members.length });
  const scoredById = new Map(result.candidates.map(c => [c.member.id, c]));

  // Substring fallback for items the matcher didn't score
  const lowerQ = query.toLowerCase().trim();
  const extras = [];
  for (const m of members) {
    if (scoredById.has(m.id)) continue;
    const hay = [m.name, m.email, m.role, m.department].filter(Boolean).join(' ').toLowerCase();
    if (hay.includes(lowerQ)) {
      extras.push({ member: m, score: null, tier: null, matchedFields: [] });
    }
  }

  return [...result.candidates, ...extras].slice(0, maxResults);
}

/**
 * Score all requirements against a free-text query, returning ranked candidates.
 *
 * @param {string} query
 * @param {Array} requirements
 * @param {{ maxResults?: number }} [options]
 * @returns {Array<{ requirement, score, tier, matchedWords }>}
 */
export function scoreRequirementsByQuery(query, requirements, options = {}) {
  const { maxResults = 12 } = options;
  if (!query || !requirements?.length) return [];

  const result = matchRequirementWithCandidates(
    { course_title: query },
    requirements,
    { maxCandidates: requirements.length }
  );
  const scoredById = new Map(result.candidates.map(c => [c.requirement.id, c]));

  const lowerQ = query.toLowerCase().trim();
  const extras = [];
  for (const r of requirements) {
    if (scoredById.has(r.id)) continue;
    if ((r.name || '').toLowerCase().includes(lowerQ)) {
      extras.push({ requirement: r, score: null, tier: null, matchedWords: [] });
    }
  }

  return [...result.candidates, ...extras].slice(0, maxResults);
}

// ---------------------------------------------------------------------------
// Utility
// ---------------------------------------------------------------------------

/**
 * Coerce a value to an array.
 */
function toArray(val) {
  if (val == null) return [];
  return Array.isArray(val) ? val : [val];
}
